/**
 * Canonical plan tier definitions with default limits.
 * Stripe product/price IDs are configured per-environment.
 */

import type { PlanTier, PlanLimits, UsageMetric } from './billing-types';

export interface PlanDefinition {
	id: string;
	tier: PlanTier;
	name: string;
	priceCents: number | null;
	isPerSeat: boolean;
	isContactSales: boolean;
	limits: PlanLimits;
	features: string[];
}

export const PLAN_DEFINITIONS: Record<PlanTier, PlanDefinition> = {
	free: {
		id: 'plan_free',
		tier: 'free',
		name: 'Free',
		priceCents: 0,
		isPerSeat: false,
		isContactSales: false,
		limits: {
			max_builds: 5,
			max_iterations: 20,
			max_hosted_apps: 1,
			max_members: 1,
			custom_domains: false,
			slack_discord_integrations: false,
			complex_app_hosting: false,
			deploy_to_own_cf: false,
			allows_overages: false,
		},
		features: [
			'5 builds per month',
			'20 iterations per month',
			'1 hosted app',
			'Community support',
		],
	},
	pro: {
		id: 'plan_pro',
		tier: 'pro',
		name: 'Pro',
		priceCents: 2900,
		isPerSeat: false,
		isContactSales: false,
		limits: {
			max_builds: 50,
			max_iterations: 500,
			max_hosted_apps: 10,
			max_members: 1,
			custom_domains: true,
			slack_discord_integrations: false,
			complex_app_hosting: true,
			deploy_to_own_cf: false,
			allows_overages: true,
		},
		features: [
			'50 builds per month',
			'500 iterations per month',
			'10 hosted apps',
			'Custom domains',
			'Complex app hosting',
			'Overage billing',
			'Priority support',
		],
	},
	team: {
		id: 'plan_team',
		tier: 'team',
		name: 'Team',
		priceCents: 4900,
		isPerSeat: true,
		isContactSales: false,
		limits: {
			max_builds: 200,
			max_iterations: 2000,
			max_hosted_apps: 50,
			max_members: null,
			custom_domains: true,
			slack_discord_integrations: true,
			complex_app_hosting: true,
			deploy_to_own_cf: true,
			allows_overages: true,
		},
		features: [
			'200 builds per month',
			'2,000 iterations per month',
			'50 hosted apps',
			'Unlimited team members',
			'Custom domains',
			'Slack & Discord integrations',
			'Deploy to your own Cloudflare',
			'Overage billing',
			'Priority support',
		],
	},
	enterprise: {
		id: 'plan_enterprise',
		tier: 'enterprise',
		name: 'Enterprise',
		priceCents: null,
		isPerSeat: true,
		isContactSales: true,
		limits: {
			max_builds: null,
			max_iterations: null,
			max_hosted_apps: null,
			max_members: null,
			custom_domains: true,
			slack_discord_integrations: true,
			complex_app_hosting: true,
			deploy_to_own_cf: true,
			allows_overages: true,
		},
		features: [
			'Unlimited builds',
			'Unlimited iterations',
			'Unlimited hosted apps',
			'Unlimited team members',
			'Custom domains',
			'Slack & Discord integrations',
			'Deploy to your own Cloudflare',
			'Dedicated support',
			'SLA & custom contracts',
		],
	},
};

/**
 * Get the default plan limits for a given tier.
 */
export function getPlanLimits(tier: PlanTier): PlanLimits {
	return PLAN_DEFINITIONS[tier].limits;
}

/**
 * Typed mapping from usage metric to its corresponding PlanLimits key.
 * Avoids unsafe `max_${metric}` string interpolation.
 */
export const METRIC_LIMIT_KEYS: Record<UsageMetric, keyof PlanLimits> = {
	builds: 'max_builds',
	iterations: 'max_iterations',
	hosted_apps: 'max_hosted_apps',
} as const;

/**
 * Get the usage limit for a specific metric on a given tier.
 * Returns null for unlimited.
 */
export function getMetricLimit(tier: PlanTier, metric: UsageMetric): number | null {
	const limits = PLAN_DEFINITIONS[tier].limits;
	const value = limits[METRIC_LIMIT_KEYS[metric]];
	if (typeof value === 'number') return value;
	return null;
}

/**
 * Get the start of the current billing period (first of the month) as a Unix timestamp in seconds.
 * Uses UTC to avoid local timezone issues outside of Cloudflare Workers.
 */
export function getCurrentPeriodStart(): number {
	const now = new Date();
	return Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) / 1000);
}
